import { useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import CircularProgress from '@material-ui/core/CircularProgress';
import { UserIdentity } from '@backstage/core-components';
import {
  useApi,
  githubAuthApiRef,
  googleAuthApiRef,
  ProfileInfoApi,
  BackstageIdentityApi,
  SessionApi,
} from '@backstage/core-plugin-api';
import type { SignInPageProps } from '@backstage/core-plugin-api';
import { useMountEffect } from '@react-hookz/web';

type SignInAuthApi = ProfileInfoApi & BackstageIdentityApi & SessionApi;

const useStyles = makeStyles({
  root: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 24,
    background: '#050505',
    backgroundImage: [
      'radial-gradient(ellipse 60% 50% at 50% -10%, rgba(143, 147, 255, 0.28), transparent)',
      'radial-gradient(ellipse 45% 40% at 85% 110%, rgba(46, 43, 128, 0.55), transparent)',
      'radial-gradient(ellipse 40% 35% at 10% 95%, rgba(143, 147, 255, 0.12), transparent)',
    ].join(', '),
  },
  card: {
    width: '100%',
    maxWidth: 400,
    padding: '48px 40px 36px',
    borderRadius: 20,
    background: 'rgba(255, 255, 255, 0.04)',
    border: '1px solid rgba(255, 255, 255, 0.09)',
    backdropFilter: 'blur(18px)',
    boxShadow:
      '0 24px 80px rgba(0, 0, 0, 0.55), 0 0 0 1px rgba(143, 147, 255, 0.06)',
    textAlign: 'center',
  },
  logo: {
    width: 64,
    height: 64,
    borderRadius: 15,
    boxShadow: '0 8px 32px rgba(143, 147, 255, 0.25)',
  },
  title: {
    marginTop: 20,
    color: '#fff',
    fontSize: 24,
    fontWeight: 700,
    letterSpacing: '-0.01em',
  },
  subtitle: {
    marginTop: 8,
    marginBottom: 32,
    color: 'rgba(255, 255, 255, 0.55)',
    fontSize: 14.5,
    lineHeight: 1.5,
  },
  button: {
    width: '100%',
    height: 46,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 12,
    borderRadius: 10,
    fontSize: 15,
    fontWeight: 500,
    fontFamily: 'inherit',
    cursor: 'pointer',
    transition:
      'transform 0.12s ease, box-shadow 0.12s ease, background 0.12s ease',
    '&:hover': { transform: 'translateY(-1px)' },
    '&:active': { transform: 'translateY(0)' },
    '&:disabled': { opacity: 0.6, cursor: 'default', transform: 'none' },
  },
  // Official Google button style: white, subtle border, dark text.
  googleButton: {
    background: '#fff',
    color: '#1F1F1F',
    border: '1px solid rgba(0, 0, 0, 0.15)',
    '&:hover': { boxShadow: '0 6px 20px rgba(255, 255, 255, 0.18)' },
  },
  // Official GitHub button style: dark, white mark and text.
  githubButton: {
    background: '#24292F',
    color: '#fff',
    border: '1px solid rgba(255, 255, 255, 0.18)',
    '&:hover': {
      background: '#2F363D',
      boxShadow: '0 6px 20px rgba(0, 0, 0, 0.4)',
    },
  },
  divider: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    margin: '20px 0',
    color: 'rgba(255, 255, 255, 0.35)',
    fontSize: 12,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    '&::before, &::after': {
      content: '""',
      flex: 1,
      height: 1,
      background: 'rgba(255, 255, 255, 0.12)',
    },
  },
  hint: {
    marginTop: 10,
    color: 'rgba(255, 255, 255, 0.4)',
    fontSize: 12.5,
  },
  error: {
    marginTop: 18,
    color: '#FF385F',
    fontSize: 13.5,
  },
  spinner: {
    color: '#8F93FF',
  },
});

const GoogleIcon = () => (
  <svg width="20" height="20" viewBox="0 0 48 48" aria-hidden="true">
    <path
      fill="#EA4335"
      d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
    />
    <path
      fill="#4285F4"
      d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
    />
    <path
      fill="#FBBC05"
      d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"
    />
    <path
      fill="#34A853"
      d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
    />
  </svg>
);

const GitHubIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 16 16"
    fill="#fff"
    aria-hidden="true"
  >
    <path d="M8 0C3.58 0 0 3.58 0 8c0 3.54 2.29 6.53 5.47 7.59.4.07.55-.17.55-.38 0-.19-.01-.82-.01-1.49-2.01.37-2.53-.49-2.69-.94-.09-.23-.48-.94-.82-1.13-.28-.15-.68-.52-.01-.53.63-.01 1.08.58 1.23.82.72 1.21 1.87.87 2.33.66.07-.52.28-.87.51-1.07-1.78-.2-3.64-.89-3.64-3.95 0-.87.31-1.59.82-2.15-.08-.2-.36-1.02.08-2.12 0 0 .67-.21 2.2.82.64-.18 1.32-.27 2-.27.68 0 1.36.09 2 .27 1.53-1.04 2.2-.82 2.2-.82.44 1.1.16 1.92.08 2.12.51.56.82 1.27.82 2.15 0 3.07-1.87 3.75-3.65 3.95.29.25.54.73.54 1.48 0 1.07-.01 1.93-.01 2.2 0 .21.15.46.55.38A8.012 8.012 0 0 0 16 8c0-4.42-3.58-8-8-8z" />
  </svg>
);

/**
 * SAVR-branded sign-in page with official-style Google and GitHub buttons.
 * Google is the internal path (@savr.com), GitHub is for allowlisted external
 * partners (org/external-users.yaml).
 */
export const SavrSignInPage = ({ onSignInSuccess }: SignInPageProps) => {
  const classes = useStyles();
  const googleApi = useApi(googleAuthApiRef);
  const githubApi = useApi(githubAuthApiRef);
  const [checking, setChecking] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();

  const completeSignIn = async (
    authApi: SignInAuthApi,
    instantPopup: boolean,
    provider: string,
  ) => {
    const identityResponse = await authApi.getBackstageIdentity(
      instantPopup ? { instantPopup: true } : { optional: true },
    );
    if (!identityResponse) {
      return false;
    }
    const profile = await authApi.getProfile();
    try {
      // Read by the analytics api to tag savr.backstage.sessions by provider.
      localStorage.setItem('savr.signInProvider', provider);
    } catch {
      /* private mode etc - the tag just falls back to unknown */
    }
    onSignInSuccess(
      UserIdentity.create({
        identity: identityResponse.identity,
        authApi,
        profile,
      }),
    );
    return true;
  };

  // Silently resume an existing session so returning users skip the buttons.
  useMountEffect(async () => {
    try {
      if (await completeSignIn(googleApi, false, 'google')) return;
      if (await completeSignIn(githubApi, false, 'github')) return;
    } catch {
      // Fall through to the buttons.
    }
    setChecking(false);
  });

  const handleLogin = async (authApi: SignInAuthApi, provider: string) => {
    setError(undefined);
    setBusy(true);
    try {
      await completeSignIn(authApi, true, provider);
    } catch (err: any) {
      if (err?.name !== 'PopupRejectedError') {
        setError(err?.message ?? 'Sign-in failed');
      }
      setBusy(false);
    }
  };

  if (checking) {
    return (
      <div className={classes.root}>
        <CircularProgress className={classes.spinner} />
      </div>
    );
  }

  return (
    <div className={classes.root}>
      <div className={classes.card}>
        <img className={classes.logo} src="/savr-icon.svg" alt="SAVR" />
        <Typography className={classes.title} component="h1">
          SAVR Developer Portal
        </Typography>
        <Typography className={classes.subtitle}>
          Sign in to explore services, APIs and documentation
        </Typography>

        <button
          className={`${classes.button} ${classes.googleButton}`}
          onClick={() => handleLogin(googleApi, 'google')}
          disabled={busy}
        >
          <GoogleIcon />
          Sign in with Google
        </button>

        <div className={classes.divider}>or</div>

        <button
          className={`${classes.button} ${classes.githubButton}`}
          onClick={() => handleLogin(githubApi, 'github')}
          disabled={busy}
        >
          <GitHubIcon />
          Sign in with GitHub
        </button>
        <Typography className={classes.hint}>For external partners</Typography>

        {error && <Typography className={classes.error}>{error}</Typography>}
      </div>
    </div>
  );
};
